import { useState } from 'react';
import { useCopy } from '../../i18n';
import { MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { supabase } from '../../services/supabaseClient';
import { formatPhoneBR, normalizePhoneBR, isValidPhoneBR } from '../../utils/phone';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Gate de WhatsApp obrigatório pós-login, SÓ PARA CADASTROS NOVOS.
 *
 * O cadastro por email já exige o telefone, mas o cadastro via Google (OAuth)
 * não passa pelo formulário. Este overlay captura o número de quem entrou pelo
 * Google a partir do lançamento da feature. A base ANTERIOR fica isenta — não
 * travamos quem já usa o produto. Admins também isentos. Sem botão de fechar.
 *
 * GATE_CUTOFF: data de lançamento (deploy) desta feature. Só usuários criados
 * a partir dela são obrigados. Ajuste para a data real do deploy em produção.
 */
const GATE_CUTOFF = '2026-06-11'; // YYYY-MM-DD — base criada antes disso fica livre

export default function CompleteWhatsappModal() {
  const t = useCopy().auth;
  const { user, profile, refreshProfile } = useAuth();
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Aparece só para: logado, perfil carregado, SEM WhatsApp, não-admin e
  // cadastrado A PARTIR do lançamento (não incomoda a base existente).
  if (!user || !profile || profile.phone_number || profile.is_admin) return null;
  if (!profile.created_at || profile.created_at.slice(0, 10) < GATE_CUTOFF) return null;

  const handleSave = async () => {
    if (!isValidPhoneBR(phone)) {
      setError(t.validation.invalidWhatsapp);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: updErr } = await supabase
        .from('profiles')
        .update({
          phone_number: normalizePhoneBR(phone),
          whatsapp_consent: consent,
          whatsapp_consent_at: consent ? new Date().toISOString() : null,
        })
        .eq('id', user.id);
      if (updErr) throw updErr;
      await refreshProfile();
      // refreshProfile atualiza o profile no contexto → este componente desmonta.
    } catch {
      setError(t.errors.saveFailed);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-[420px] bg-white rounded-[var(--radius-lg)] shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 text-center">
        <div className="flex justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-[rgba(126,62,238,0.1)] flex items-center justify-center">
            <MessageCircle size={28} className="text-[var(--color-primary)]" />
          </div>
        </div>
        <h2 className="text-[var(--font-xl)] font-bold text-[#1a1a2e] m-0 mb-2">{t.whatsappGate.title}</h2>
        <p className="text-sm text-[var(--color-gray-500)] m-0 mb-6 leading-relaxed">
          {t.whatsappGate.subtitle}
        </p>

        <div className="flex flex-col gap-2 text-left mb-4">
          <Label htmlFor="complete-phone" className="text-sm font-medium text-[var(--color-gray-700)]">{t.field.whatsapp}</Label>
          <Input
            id="complete-phone"
            type="tel"
            inputMode="numeric"
            autoFocus
            value={phone}
            onChange={(e) => setPhone(formatPhoneBR(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            placeholder={t.field.whatsappPlaceholder}
            className="py-3 px-4 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_rgba(126,62,238,0.15)]"
          />
        </div>

        <label className="flex items-start gap-2 text-left mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-[var(--color-primary)]"
          />
          <span className="text-xs text-[var(--color-gray-500)] leading-snug">
            {t.whatsappGate.consent}
          </span>
        </label>

        {error && <p className="text-xs text-[var(--color-danger)] m-0 mb-3">{error}</p>}

        <Button
          variant="primary"
          size="xl"
          className="w-full"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t.loading.saving : t.action.save}
        </Button>
      </div>
    </div>
  );
}
